// Sign in / sign up / verify / reset flows. Presented when there's no session and the
// user has tapped "Sign in" from the profile tab. We still support a
// "Continue as guest" path — guest data is stored locally and migrated on sign-in.
//
// Sign-up confirmation now uses a 6-digit OTP code instead of an email
// link. After "Create account", we switch to a 'verify' step where the
// user types the code from their email. Verifying creates the session
// in-app, so there's no Safari roundtrip and no broken-link page to
// confuse users.

import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { useAuth } from './useAuth.js';

export function AuthScreen({ initialMode = 'signin', onGuest }) {
  const { signIn, signUp, verifySignupOtp, resetPassword, resendConfirmation } = useAuth();
  const [mode, setMode] = useState(initialMode); // 'signin' | 'signup' | 'verify' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(email.trim(), password);
        // `useAuth` handles the transition; nothing else to do here.
      } else if (mode === 'signup') {
        if (password.length < 8) throw new Error('Password must be at least 8 characters.');
        // Two-field confirmation prevents typos that would otherwise lock
        // the user out of an account they just created. Compare exact
        // strings; do NOT trim, since spaces are valid password chars and
        // trimming silently could create a mismatch with sign-in later.
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match.');
        }
        try {
          await signUp(email.trim(), password);
          // Switch to OTP entry. The 6-digit code is in the email Supabase
          // just sent; user types it on the next screen and we verify
          // in-app. No browser roundtrip.
          setOtp('');
          setMode('verify');
          setInfo(`We sent a 6-digit code to ${email.trim()}. Type it here to finish creating your account.`);
        } catch (err) {
          // Existing-email case: useAuth.signUp throws an error tagged
          // with `code: 'EMAIL_EXISTS'`. Bounce the user onto the sign-in
          // screen with the email pre-filled, password cleared, and a
          // friendly explainer instead of leaving them stranded on a
          // verify-code screen waiting for an email that won't arrive.
          if (err && err.code === 'EMAIL_EXISTS') {
            setPassword('');
            setConfirmPassword('');
            setMode('signin');
            setError('');
            setInfo('That email is already registered. Sign in below.');
            return;
          }
          throw err;
        }
      } else if (mode === 'verify') {
        const code = otp.replace(/\D/g, '');
        if (code.length !== 6) throw new Error('Enter the 6-digit code from your email.');
        await verifySignupOtp(email.trim(), code);
        // useAuth flips status to 'signed-in' on success.
      } else if (mode === 'forgot') {
        await resetPassword(email.trim());
        setInfo('If an account exists for that email, a reset link is on its way.');
      }
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      await resendConfirmation(email.trim());
      setInfo('Fresh code sent. Check your email.');
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <form onSubmit={submit} className="auth-card">
        <div className="auth-logo">
          <Icon name="sparkles" size={26} color="#fff" />
        </div>
        <h1 className="auth-title">
          {mode === 'signup' ? 'Create your account' :
           mode === 'verify' ? 'Verify your email' :
           mode === 'forgot' ? 'Reset password' :
           'Welcome back'}
        </h1>
        <p className="auth-sub">
          {mode === 'signup'
            ? 'Sync your habits, agendas, and streaks across devices.'
            : mode === 'verify'
              ? `Type the 6-digit code we just sent to ${email}.`
              : mode === 'forgot'
                ? "We'll send a reset link to your email."
                : 'Sign in to sync your progress.'}
        </p>

        {/* Email + password fields are hidden during the verify step
            (the email is already locked in from the signup submit, and
            password isn't needed to verify the OTP). */}
        {mode !== 'verify' && (
          <div className="auth-field">
            <input
              className="input"
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
        )}

        {mode !== 'forgot' && mode !== 'verify' && (
          <div className="auth-field">
            <input
              className="input"
              type="password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={mode === 'signup' ? 8 : 1}
            />
          </div>
        )}

        {/* Confirm-password field. Signup only — pointless on sign-in
            since the user already has the credential, and password reset
            doesn't take a password at this step (the email link does).
            Same autoComplete hint as the primary field so iCloud Keychain
            offers the same suggestion in both. The mismatch check runs in
            submit() before we hit Supabase. */}
        {mode === 'signup' && (
          <div className="auth-field">
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              aria-invalid={confirmPassword.length > 0 && confirmPassword !== password ? 'true' : undefined}
            />
            {confirmPassword.length > 0 && confirmPassword !== password && (
              <div
                style={{
                  marginTop: 6,
                  fontFamily: 'var(--font-body)',
                  fontSize: 11.5,
                  color: '#ff8c8c',
                }}
              >
                Passwords don&apos;t match yet.
              </div>
            )}
          </div>
        )}

        {/* OTP input for the verify step. inputMode + pattern + maxLength
            put up the numeric keypad on iOS and discourage non-digit
            entry without blocking paste. autoComplete="one-time-code"
            lets iOS Mail/Messages offer the code as a quick autofill
            chip above the keyboard. */}
        {mode === 'verify' && (
          <div className="auth-field">
            <input
              className="input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              autoFocus
              style={{
                textAlign: 'center', letterSpacing: '0.32em',
                fontFamily: 'ui-monospace, SF Mono, Menlo, Consolas, monospace',
                fontSize: 22, fontWeight: 700,
              }}
            />
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}
        {info && <div className="auth-info">{info}</div>}

        <button className="auth-submit" type="submit" disabled={busy}>
          {busy ? 'One moment…' :
           mode === 'signup' ? 'Create account' :
           mode === 'verify' ? 'Verify and sign in' :
           mode === 'forgot' ? 'Send reset link' :
           'Sign in'}
        </button>

        {mode === 'verify' && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              type="button"
              className="auth-link"
              onClick={resendCode}
              disabled={busy}
            >
              Didn’t get the code? Send a new one
            </button>
          </div>
        )}

        {mode === 'signin' && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button type="button" className="auth-link" onClick={() => { setError(''); setInfo(''); setMode('forgot'); }}>
              Forgot your password?
            </button>
          </div>
        )}

        <div className="auth-switch">
          {mode === 'signin' && <>
            New here?
            <button type="button" className="auth-link" onClick={() => { setError(''); setInfo(''); setConfirmPassword(''); setMode('signup'); }}>
              Create an account
            </button>
          </>}
          {mode === 'signup' && <>
            Already have an account?
            <button type="button" className="auth-link" onClick={() => { setError(''); setInfo(''); setConfirmPassword(''); setMode('signin'); }}>
              Sign in
            </button>
          </>}
          {mode === 'forgot' && <>
            Remembered it?
            <button type="button" className="auth-link" onClick={() => { setError(''); setInfo(''); setMode('signin'); }}>
              Back to sign in
            </button>
          </>}
          {mode === 'verify' && <>
            Wrong email?
            <button type="button" className="auth-link" onClick={() => { setError(''); setInfo(''); setOtp(''); setMode('signup'); }}>
              Edit email
            </button>
          </>}
        </div>

        {onGuest && (
          <div style={{ textAlign: 'center', marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--stroke-1)' }}>
            <button type="button" className="auth-link" onClick={onGuest}>
              Continue without an account →
            </button>
            <div style={{ marginTop: 4, fontFamily: 'var(--font-body)', fontSize: 10.5, color: 'var(--fg-4)' }}>
              Your data stays on this device. Sign in later to sync.
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
